/* eslint-disable react-refresh/only-export-components -- shared utils module that also exports the tiny <PlayerLink> / <RecordTimeLink>; HMR fast-refresh doesn't apply here. */
import type { CSSProperties, ReactNode } from 'react';
import { Link } from 'react-router-dom';

import type { GameType } from '@ottd/api';

import { activeLocale, intlLocale, tr } from '@/lib/locale';
import { serverTypeTheme } from '@/lib/server-type';
import { cn } from '@/lib/utils';

/*
 * Small helpers shared across the public screens: banner styling, goal parsing,
 * the player-name link, and date formatters for the ISO fields the API returns
 * raw. (One module so `@/pages/shared` resolves unambiguously.)
 */

/**
 * Background style for a server/game image banner: the type's tint gradient
 * layered over the shared pixel-art background. Degrades to the tint alone if
 * the image is missing.
 */
export function bannerStyle(type: GameType | null | undefined, position = 'center'): CSSProperties {
  const theme = serverTypeTheme(type);
  return {
    backgroundImage: `${theme.tint}, url(/bg/bg_1440.jpg)`,
    backgroundSize: 'cover',
    backgroundPosition: position,
    imageRendering: 'pixelated',
  };
}

/**
 * Extracts the numeric target from a preformatted goal label (e.g.
 * "2,000 town population" → 2000, "€25,000,000 company value" → 25000000).
 * Returns `null` when the label carries no plain digits (e.g. "1.2M €").
 */
export function parseGoalNumber(goal: string | null | undefined): number | null {
  if (!goal) return null;
  const digits = goal.replace(/[^\d]/g, '');
  if (digits.length === 0) return null;
  const value = Number(digits);
  return Number.isFinite(value) && value > 0 ? value : null;
}

export function PlayerLink({
  name,
  className,
  children,
}: {
  name: string;
  className?: string;
  children?: ReactNode;
}) {
  return (
    <Link
      to={`/player/${encodeURIComponent(name)}`}
      className={cn('text-green hover:brightness-118', className)}
    >
      {children ?? name}
    </Link>
  );
}

/**
 * A server's best time, linked to the game that set it; the bare time when
 * the API names no record game.
 */
export function RecordTimeLink({
  time,
  gameId,
  className,
}: {
  time: string;
  gameId: number | null;
  className?: string;
}) {
  if (gameId === null) return <span className={className}>{time}</span>;

  return (
    <Link to={`/game/${gameId}`} className={cn('hover:brightness-118', className)}>
      {time}
    </Link>
  );
}

/** ISO timestamp → "14:05" (24h); "--:--" when null/invalid. */
export function formatClockTime(iso: string | null | undefined): string {
  const unknown = tr('common:time.clockUnknown');
  if (!iso) return unknown;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return unknown;
  return date.toLocaleTimeString(intlLocale(activeLocale()), {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

/** ISO date → "Feb 2024"; em dash when null/invalid. */
export function formatMonthYear(iso: string | null | undefined): string {
  const unknown = tr('common:time.unknown');
  if (!iso) return unknown;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return unknown;
  return date.toLocaleDateString(intlLocale(activeLocale()), {
    month: 'short',
    year: 'numeric',
  });
}

/** ISO date → "1 Jul 2026"; em dash when null/invalid. */
export function formatDate(iso: string | null | undefined): string {
  const unknown = tr('common:time.unknown');
  if (!iso) return unknown;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return unknown;
  return date.toLocaleDateString(intlLocale(activeLocale()), {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

/** "1 Jul – 31 Jul 2026" for a season's start/end. */
export function formatDateRange(
  start: string | null | undefined,
  end: string | null | undefined,
): string {
  const unknown = tr('common:time.unknown');
  const from = formatDate(start);
  const to = formatDate(end);
  if (from === unknown && to === unknown) return unknown;
  if (from === unknown) return to;
  if (to === unknown) return from;
  return `${from} – ${to}`;
}
